import React, { useState, useMemo, useEffect, useImperativeHandle, forwardRef } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import Utility from '../../../../Utility/Utility';
import { createCustomerHotelBooking } from '../../Booking/Controller/BookingController';
import '../Assets/css/HotelOverviewCard.scss';

const Stepper = ({ value, onChange, min = 0 }) => (
    <span className='hocStepper'>
        <button type='button' onClick={() => onChange(Math.max(min, value - 1))}>−</button>
        <span>{value}</span>
        <button type='button' onClick={() => onChange(value + 1)}>+</button>
    </span>
);

const HotelOverviewCard = forwardRef(function HotelOverviewCard({
    branchData = {}, searchData = {}, onProceed, onPrevious,
    onPassengerChange, customerFormData = {}, userCredentials = {},
    isGuestFlag = false, loaderStateTrue, loaderStateFalse, onBookingSuccess
}, ref) {
    const history = useHistory();
    if (!branchData || Object.keys(branchData).length === 0) return null;

    const details = branchData.branch_full_details || {};
    const currency = details.currency || branchData.currency || 'INR';
    const symbol = Utility.getCurrencySymbol(currency);

    // Stay details (editable)
    const [startDate, setStartDate] = useState(searchData.start_date ? moment(searchData.start_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(searchData.end_date ? moment(searchData.end_date).format('YYYY-MM-DD') : moment().add(1, 'days').format('YYYY-MM-DD'));
    const [adults, setAdults] = useState(Number(searchData.adult) || 1);
    const [children, setChildren] = useState(Number(searchData.children) || 0);

    const nights = Math.max(1, moment(endDate).diff(moment(startDate), 'days') || 1);
    const stayDates = Array.from({ length: nights }, (_, i) => moment(startDate).add(i, 'days'));

    // Room + rate plan selection
    const roomTypes = branchData.roomtype_details || [];
    const seedSelected = () => {
        const selected = {};
        const selectedRoom = searchData.selectedRoom || {};
        Object.entries(selectedRoom).forEach(([gId, group]) => {
            Object.entries(group || {}).forEach(([rId, rv]) => {
                if (Number(rv?.value) > 0) selected[`${gId}_${rId}`] = Number(rv.value);
            });
        });
        if (!Object.keys(selected).length && roomTypes[0]) {
            const firstRate = roomTypes[0].RoomRates?.[0];
            if (firstRate) selected[`${roomTypes[0].id}_${firstRate.id}`] = 1;
        }
        return selected;
    };
    const [roomSelection, setRoomSelection] = useState(seedSelected);

    const toggleRoom = (room, rate) => {
        const key = `${room.id}_${rate.id}`;
        setRoomSelection((prev) => {
            const next = { ...prev };
            if (next[key]) delete next[key];
            else next[key] = 1;
            return next;
        });
    };

    const selectedRoomRows = useMemo(() => {
        const rows = [];
        roomTypes.forEach((room) => {
            (room.RoomRates || []).forEach((rate) => {
                const key = `${room.id}_${rate.id}`;
                const qty = Number(roomSelection[key]) || 0;
                if (qty > 0) rows.push({ key, room, rate, qty });
            });
        });
        return rows;
    }, [roomSelection, roomTypes]);

    const nightRate = (key, date, baseRate) => Number(baseRate) || 0;

    const roomCharges = selectedRoomRows.reduce((sum, { key, rate, qty }) => {
        return sum + stayDates.reduce((s, d) => s + nightRate(key, d.format('YYYY-MM-DD'), rate.price || rate.selling_rate_per_night) * qty, 0);
    }, 0);
    const [taxApplicability, setTaxApplicability] = useState('CGST_SGST');
    const [bookingResult, setBookingResult] = useState(null);
    const [copied, setCopied] = useState(false);
    const isIgst = taxApplicability === 'IGST';
    const cgstAmt = isIgst ? 0 : (roomCharges * 9) / 100;
    const sgstAmt = isIgst ? 0 : (roomCharges * 9) / 100;
    const igstAmt = isIgst ? (roomCharges * 18) / 100 : 0;
    const gstTotal = cgstAmt + sgstAmt + igstAmt;
    const totalAmount = roomCharges + gstTotal;

    // Minimum advance %: hotel booking rule if present, else 25
    const minimumPercent = Number(branchData?.branch_wise_booking_rule?.booking_advance_percent) || 25;

    // Report totals up to the payment modal (grandTotal × minimumPercent = minimum payable)
    useEffect(() => {
        if (typeof onPassengerChange === 'function') {
            onPassengerChange({ adults, children, grandTotal: totalAmount, minimumPercent, startDate, endDate });
        }
    }, [adults, children, totalAmount, minimumPercent, startDate, endDate]);

    useImperativeHandle(ref, () => ({
        validateBeforePayment: () => {
            if (selectedRoomRows.length === 0) {
                Utility.toastNotifications('Please select at least one room & rate plan.', 'Warning', 'warning');
                return false;
            }
            return true;
        },
        confirmBooking: handleConfirmBooking
    }));

    // POST api/customer/hotel-bookings after payment entry (called via ref from the payment modal)
    const handleConfirmBooking = async (paymentList = []) => {
        if (selectedRoomRows.length === 0) {
            Utility.toastNotifications('Please select at least one room & rate plan.', 'Warning', 'warning');
            return;
        }

        let custId = 0, custName = '', custPhone = '', custEmail = '', custGstin = '', compName = '', compAddr = '';
        if (!isGuestFlag && userCredentials && Object.keys(userCredentials).length > 0) {
            custId = userCredentials.id || 0;
            const uDet = userCredentials.user_details || {};
            custName = `${uDet.first_name || ''} ${uDet.last_name || ''}`.trim() || userCredentials.name || '';
            custPhone = uDet.contact_number || userCredentials.contact_number || '';
            custEmail = uDet.user_email || userCredentials.email || '';
        } else if (customerFormData && Object.keys(customerFormData).length > 0) {
            custName = `${customerFormData.name || ''} ${customerFormData.last_name || ''}`.trim();
            custPhone = customerFormData.phone_number || '';
            custEmail = (customerFormData.email && customerFormData.email[0] && customerFormData.email[0].email) || '';
            custGstin = customerFormData.customer_gstin_no || '';
            compName = customerFormData.company_name || '';
            compAddr = customerFormData.company_address || '';
        }

        const childArr = (searchData.children_age || []).map((v) => ({ age: Number(v?.age?.value ?? v?.age ?? v) || 0, type: 'child' }));
        while (childArr.length < children) childArr.push({ age: 0, type: 'child' });
        if (childArr.length > children) childArr.length = children;

        const paymentArr = (paymentList || []).map((p) => ({
            amount: String(p.amount ?? ''),
            paid_amount: String(p.amount ?? ''),
            payment_mode: p.payment_mode || 'cash',
            payment_date: moment(p.payment_date).format('YYYY-MM-DD'),
            transaction_date: moment(p.transaction_date || p.payment_date).format('YYYY-MM-DD'),
            reference_id: p.payment_ref_id || p.reference_id || '',
            comments: p.comments || ''
        }));

        const bookingPayload = selectedRoomRows.map(({ room, rate, qty }) => ({
            org_id: details.org_id ?? branchData?.org_id,
            hotel_property_id: branchData?.hotel_property_id ?? details.hotel_property_id,
            room_type_id: room.id,
            meal_plan_id: rate.meal_plan_id ?? rate.MealPlanData?.id,
            check_in: moment(startDate).format('YYYY-MM-DD'),
            check_out: moment(endDate).format('YYYY-MM-DD'),
            no_of_rooms: qty,
            adult: adults,
            child: childArr,
            extra_adults: 0,
            extra_children: 0,
            extra_beds: 0,
            customer_name: custName,
            customer_phone: custPhone,
            customer_email: custEmail,
            customer_id: custId,
            customer_gstin_no: custGstin,
            customer_address: '',
            company_name: compName,
            company_address: compAddr,
            valid_until: moment().format('YYYY-MM-DD'),
            markup_type: 'percentage',
            markup_value: 0,
            agency_markup_type: 'percentage',
            agency_markup_value: 0,
            special_discount_by_percentage: 0,
            special_discount_by_amount: 0,
            no_tax: false,
            is_igst_applicable: isIgst,
            gst_data: {
                IGST: isIgst ? 18 : 0,
                CGST: isIgst ? 0 : 9,
                SGST: isIgst ? 0 : 9
            },
            minimum_percent: minimumPercent,
            hold_inventory: false,
            notes: '',
            inclusions: [],
            exclusions: [],
            cancellation_policy: null,
            extras: [],
            payment_arr: paymentArr
        }));

        try {
            if (typeof loaderStateTrue === 'function') loaderStateTrue();
            const response = await createCustomerHotelBooking(bookingPayload);
            if (typeof loaderStateFalse === 'function') loaderStateFalse();

            if (response && (response.success || (Array.isArray(response) && response[0]?.success))) {
                const msg = response.message || (Array.isArray(response) ? response[0]?.message : 'Booking Created Successfully');
                Utility.toastNotifications(msg, 'Success', 'success');
                const resData = Array.isArray(response) ? response[0]?.data : response.data;
                setBookingResult({
                    booking_number: resData?.booking_number || resData?.booking_id || '',
                    response
                });
            } else {
                const errMsg = response?.message || (Array.isArray(response) ? response[0]?.message : 'Failed to create booking');
                Utility.toastNotifications(errMsg, 'Error', 'error');
            }
        } catch (error) {
            if (typeof loaderStateFalse === 'function') loaderStateFalse();
            const errText = error?.response?.data?.message || error?.message || 'Booking creation failed';
            Utility.toastNotifications(errText, 'Error', 'error');
        }
    };

    const handleCopyBookingNumber = async () => {
        try {
            await navigator.clipboard.writeText(bookingResult.booking_number);
        } catch (e) {
            const ta = document.createElement('textarea');
            ta.value = bookingResult.booking_number;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleBookingSuccessClose = () => {
        const res = bookingResult?.response;
        setBookingResult(null);
        if (isGuestFlag) {
            history.push(`/${localStorage.getItem('i18nextLng') || 'en'}/home`);
            return;
        }
        if (typeof onBookingSuccess === 'function') {
            onBookingSuccess(res);
        } else {
            history.push(`/${localStorage.getItem('i18nextLng') || 'en'}/booking-history`);
        }
    };

    const planLabel = (rate) => {
        const mp = rate.MealPlanData || {};
        return `${mp.plan_code || ''} ${mp.plan_name ? `(${mp.plan_name.replace(/.*\(|\)/g, '')})` : ''}`.trim() || mp.plan_name || '';
    };

    const baseRateOf = (rate) => Number(rate.price || rate.selling_rate_per_night) || 0;

    return (
        <div className='hotelOverviewCard hocCard'>
            {bookingResult && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 20000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="text-center" style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '360px', maxWidth: '92%', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
                        <div style={{ fontSize: '18px', fontWeight: '700', color: '#10b981', marginBottom: '6px' }}>Booking Created Successfully</div>
                        <div className="text-muted" style={{ fontSize: '12px' }}>Your booking number</div>
                        <div className="d-flex align-items-center justify-content-center gap-2 my-3 p-2" style={{ background: '#f1f5f9', borderRadius: '8px' }}>
                            <strong style={{ fontSize: '16px', color: '#1e293b', userSelect: 'all' }}>{bookingResult.booking_number}</strong>
                            <button type="button" className="btn btn-sm btn-outline-primary" title="Copy booking number" onClick={handleCopyBookingNumber}>
                                {copied ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                        <button type="button" className="btn btn-primary w-100" onClick={handleBookingSuccessClose}>
                            {isGuestFlag ? 'Redirect to Home' : 'View Booking History'}
                        </button>
                    </div>
                </div>
            )}
            {/* Stay Details */}
            <div className='hocSection'>
                <h6 className='hocSectionTitle'>Stay Details</h6>
                <div className='hocStayRow'>
                    <div className='hocField'>
                        <label>Check-in</label>
                        <input type='date' value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                    <div className='hocField'>
                        <label>Check-out</label>
                        <input type='date' value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                    <div className='hocField'>
                        <label>Adults</label>
                        <Stepper value={adults} min={1} onChange={setAdults} />
                    </div>
                    <div className='hocField'>
                        <label>Children</label>
                        <Stepper value={children} onChange={setChildren} />
                    </div>
                </div>
            </div>

            {/* Room & Rate Plan selection */}
            {roomTypes.length > 0 && (
                <div className='hocSection'>
                    <h6 className='hocSectionTitle'>Select Room &amp; Rate Plan</h6>
                    <div className='hocRoomCards'>
                        {roomTypes.map((room) => (
                            <div className='hocRoomCard' key={room.id}>
                                <div className='hocRoomName'>
                                    <strong>{room.room_type_name}</strong>
                                    <span className='hocRoomMeta'>
                                        <i className='fa fa-users' aria-hidden='true'></i> Max {room.max_adults} Adults, {room.max_children} Children
                                    </span>
                                </div>
                                {(room.RoomRates || []).map((rate) => {
                                    const key = `${room.id}_${rate.id}`;
                                    const checked = Number(roomSelection[key]) > 0;
                                    return (
                                        <label className='hocRateRow' key={rate.id}>
                                            <input type='checkbox' checked={checked} onChange={() => toggleRoom(room, rate)} />
                                            <span className='hocRatePlan'>
                                                <em>{planLabel(rate)}</em>
                                                <small>{symbol} {baseRateOf(rate).toFixed(0)} / night</small>
                                            </span>
                                            <span className='hocRatePrice'>{symbol} {(baseRateOf(rate) * nights * (Number(roomSelection[key]) || 1)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Rates & Charges */}
            <div className='hocSection'>
                <div className='hocTwoCol'>
                    <div>
                        <h6 className='hocSectionTitle'>Room Rates <small>({nights} {nights > 1 ? 'Nights' : 'Night'})</small></h6>
                        <table className='hocTable'>
                            <thead>
                                <tr>
                                    <th>Date</th><th>Room &amp; Rate Plan</th><th>Rooms</th><th>Night Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedRoomRows.map(({ key, room, rate }) => {
                                    const r = nightRate(key, '', rate.price || rate.selling_rate_per_night);
                                    const qty = Number(roomSelection[key]) || 1;
                                    return (
                                        <tr key={key}>
                                            <td>{stayDates[0].format('DD MMM YYYY')} - {stayDates[stayDates.length - 1].format('DD MMM YYYY')}</td>
                                            <td>{room.room_type_name}<br /><small>{planLabel(rate)}</small></td>
                                            <td>
                                                <Stepper
                                                    value={qty}
                                                    min={1}
                                                    onChange={(v) => setRoomSelection((p) => ({ ...p, [key]: v }))}
                                                />
                                            </td>
                                            <td>{symbol} {(r * nights * qty).toFixed(0)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className='hocTotals'>
                        <h6 className='hocSectionTitle'>Booking Summary</h6>
                        <div>
                            <span>GST Type</span>
                            <span className='hocGstTabs'>
                                <button type='button' className={taxApplicability === 'CGST_SGST' ? 'active' : ''} onClick={() => setTaxApplicability('CGST_SGST')}>CGST + SGST</button>
                                <button type='button' className={taxApplicability === 'IGST' ? 'active' : ''} onClick={() => setTaxApplicability('IGST')}>IGST</button>
                            </span>
                        </div>
                        <div><span>Room Charges</span><span>{symbol} {roomCharges.toFixed(2)}</span></div>
                        <div><span><strong>Tax</strong></span><span></span></div>
                        {isIgst ? (
                            <div><span>IGST (18.00%)</span><span>{symbol} {igstAmt.toFixed(2)}</span></div>
                        ) : (
                            <>
                                <div><span>CGST (9.00%)</span><span>{symbol} {cgstAmt.toFixed(2)}</span></div>
                                <div><span>SGST (9.00%)</span><span>{symbol} {sgstAmt.toFixed(2)}</span></div>
                            </>
                        )}
                        <div className='hocGrandTotal'><span>Total Amount</span><strong>{symbol} {totalAmount.toFixed(2)}</strong></div>
                    </div>
                </div>
            </div>

            <div className='hocFooter'>
                {onProceed && <button type='button' className='modbtn hocNextBtn' onClick={() => onProceed({ startDate, endDate, adults, children, roomSelection, totalAmount })}>Confirm Booking</button>}
            </div>
        </div>
    );
});

export default HotelOverviewCard;
